import { useEffect, useState } from "react";
import { ArrowLeft, Trash2 } from "lucide-react";
import {
  GenericTable,
  SearchableDropdown,
  Spinner,
  RoleValidator,
  ProductCategoryFilter,
} from "../../../Components/Common";
import {
  getRNStatusColor,
  RETURN_NOTE_STATUS,
  RETURN_NOTE_STATUS_LABELS,
  STOCK_MODAL_MODES,
} from "../constants";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { EDIT, EDIT_MRR, VIEW, VIEW_MATERIAL_REQUESTS } from "../../../routes";
import { MdModeEditOutline } from "react-icons/md";
import { useWarehouse } from "../Hooks/useWarehouse";
import { useMRR } from "../Hooks/useMRR";
import toast from "react-hot-toast";
import {
  MATERIAL_COLUMNS_MRR,
  MATERIAL_LABELS_MRR,
  MRR_STATUS,
} from "./constants";
import { getPriorityStatusColor, PRIORITY_OPTIONS, ROLES } from "../../../Constants/Strings";
import { toProperCase } from "../../../Utils";

const CreateMRRs = () => {
  const [loading, setLoading] = useState(true);
  const { newMRR, updateMRRDetails, fetchMRRById} = useMRR();
  const { fetchWarehouses } = useWarehouse();
  const [mode, setMode] = useState(STOCK_MODAL_MODES.ADD);
  const [warehouse, setWarehouse] = useState("");
  const [status, setStatus] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [formData, setFormData] = useState({
    notes: "",
    estimated_delivery_date: "",
    priority: "",
  });
  const location = useLocation();
  const [materials, setMaterials] = useState([]);
  const navigate = useNavigate();
  const param = useParams();

  const isDisabled = mode !== STOCK_MODAL_MODES.ADD

    const resetForm = () => {
      setFormData({ notes: "", estimated_delivery_date: "", priority: "" ,mrr_code:"",});
      setWarehouseId("");
      setWarehouse("");
      setMaterials([]);
      setStatus("");
    };

  const removeMaterial = (product) => {
    setMaterials((prev) => prev.filter((item) => item.id !== product.id));
    toast.success(`${product.product_name} removed form the list`);
  };

  const handleAddMaterial = (material) => {
    if (!material) return;
    setMaterials((prev) => {
      // Check if material with same id or name already exists
      const exists = prev.some(
        (item) =>
          item.id === material.id || item.product_name === material.product_name
      );
      if (exists) {
        toast.error(`${material.product_name} already added`);
        return prev;
      } else {
        toast.success(`${material.product_name} added`);
      }
      // skip adding duplicate
      return [...prev, { ...material, product: material.id, quantity: 1 }];
    });
  };

  const renderField = (column, row) => {
    switch (column) {
      case "product_name":
        return (
          <div className="flex flex-col">
            <span className="font-semibold">{row.product_name}</span>
            <span className="text-gray-500 font-normal">
              {row.specification}
            </span>
            <span className="text-gray-800 font-normal italic">
              ({row.UOM_name || row.UOM})
            </span>
          </div>
        );
      case "description":
        return (
          <div className="border-b-2 border-gray-400">
            <input
              type="text"
              value={row.description}
              placeholder="Enter note..."
              onChange={(e) =>
                setMaterials((prev) =>
                  prev.map((m) =>
                    m.id === row.id ? { ...m, description: e.target.value } : m
                  )
                )
              }
              className="w-32 h-7 bg-inherit border-none focus:ring-0  text-start"
            />
          </div>
        );
      case "quantity":
        return (
          <div className="border-b-2 border-gray-400 w-14">
            <input
              type="number"
              min="1"
              value={Number(row.quantity)}
              onChange={(e) =>
                setMaterials((prev) =>
                  prev.map((m) =>
                    m.id === row.id ? { ...m, quantity: e.target.value } : m
                  )
                )
              }
              className="w-18 h-7 bg-inherit border-none focus:ring-0 text-start"
            />
          </div>
        );
      case "actions":
        return (
          <>
            <button
              className=" hover:underline ml-2"
              onClick={() => removeMaterial(row)}
            >
              <Trash2 className="h-5 w-5 text-red-400" />
            </button>
          </>
        );
      default:
        return row[column] || "N/A";
    }
  };

  const handleSubmit = async (submitType) => {
    const missingFields = [];
    if (!warehouseId) missingFields.push("warehouse");
    if (!formData.estimated_delivery_date)
      missingFields.push("expected delivery date");
    if (!formData.notes) missingFields.push("notes");
    if(!formData.priority) missingFields.push("priority");
    if (materials.length === 0) missingFields.push("at least one material");

    if (missingFields.length > 0) {
      toast.error(`Please provide: ${missingFields.join(", ")}`);
      return;
    }

    const payload = {
      warehouse: warehouseId,
      status: submitType,
      notes: formData.notes,
      materials,
      estimated_delivery_date: formData.estimated_delivery_date,
      priority: formData.priority,
    };
    var data = null;
    if (mode === STOCK_MODAL_MODES.ADD) {
      data = await newMRR(payload);
    } else {
      data = await updateMRRDetails(param.instanceId, payload);
      setMaterials(data.materials);
      setFormData({
        notes: data.notes,
        estimated_delivery_date: data.estimated_delivery_date,
        priority: data.priority,
      });
      setWarehouse(data.warehouse_name);
      setWarehouseId(data.warehouse);
      setStatus(data.status);
    }
    navigate(VIEW_MATERIAL_REQUESTS.replace(":instanceId", data.id));
  };

  const getMRRData = async () => {
    setLoading(true);
    try {
      const data = await fetchMRRById(param.instanceId);

      if (data) {
        setFormData({
          mrr_code:data.mrr_code || "",
          notes: data.notes || "",
          estimated_delivery_date: data.estimated_delivery_date || "",
          priority: data.priority || "",
        });
        setWarehouseId(data.warehouse || "");
        setWarehouse(data.warehouse_name || "");
        setMaterials(data.materials || []);
        setStatus(data.status || "");
      } else {
        resetForm();
      }
    } catch (error) {
      resetForm();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (param.instanceId) {
      getMRRData();

      if (location.pathname.includes(EDIT)) {
        setMode(STOCK_MODAL_MODES.EDIT);
      } else if (location.pathname.includes(VIEW)) {
        setMode(STOCK_MODAL_MODES.VIEW);
      }
    } else {
      setMode(STOCK_MODAL_MODES.ADD);
      setLoading(false);
    }
  }, [param.instanceId, location.pathname]);

  if (loading)
    return (
      <div className="flex items-center justify-center h-[60vh] pt-10">
        <Spinner size={4} />
      </div>
    );

  return (
    <div className="min-h-[70vh]">
      <div className="flex justify-between items-center">
        <h2 className="flex gap-2 justify-center items-center text-xl font-semibold text-gray-800 mt-2 px-3 py-2">
          <button
            type="button"
            onClick={() => navigate(VIEW_MATERIAL_REQUESTS)}
            className="text-primary text-center text-sm font-lg"
          >
            <ArrowLeft size={23} absoluteStrokeWidth className="text-primary" />
          </button>
          {mode === STOCK_MODAL_MODES.EDIT
            ? "Edit"
            : mode === STOCK_MODAL_MODES.VIEW
            ? ""
            : "Create"}{" "}
          Material Requisition Request
        </h2>
        {mode === STOCK_MODAL_MODES.VIEW &&
          status === RETURN_NOTE_STATUS.DRAFT && (
            <button
              className="mb-4 flex gap-1 px-4 py-2 bg-primary text-white rounded hover:bg-primary transition"
              onClick={() =>
                navigate(EDIT_MRR.replace(":instanceId", param.instanceId))
              }
            >
              <MdModeEditOutline size={20} />
            </button>
          )}
      </div>
      <div className="mx-auto grid grid-cols-1 md:grid-cols-2 md:gap-6 gap-4 px-4 py-2">
        {/* Left Side — Warehouse & Notes */}
        <div className="bg-white md:border-r-2 md:pr-8">
          <h3 className="text-lg font-semibold text-primary mb-4 flex items-center gap-2">
            <span className="inline-block w-1.5 h-6 bg-primary rounded-full"></span>
            MRR Materials
          </h3>
          {mode === STOCK_MODAL_MODES.EDIT && (
            <div className="mb-3">
              <label className="block text-gray-700 font-medium md:text-sm text-xs mb-1">
                {`Requested By Site`} <sub className="text-red-500">*</sub>
              </label>
              <SearchableDropdown
                fetchFunction={(param) =>
                  fetchWarehouses({ ...param, is_site: true })
                }
                onSelect={(val) => setWarehouseId(val.id)}
                labelKey="name"
                placeholder={warehouse ? warehouse : "Select Warehouse"}
                disabled={isDisabled}
              />
            </div>)}
          <div className="flex items-center justify-between gap-4 w-full">
            {mode !== STOCK_MODAL_MODES.EDIT && (
            <div className="w-[50%]">
              <label className="block text-gray-700 font-medium md:text-sm text-xs mb-1">
                {`Requested By Site`} <sub className="text-red-500">*</sub>
              </label>
              <SearchableDropdown
                fetchFunction={(param) =>
                  fetchWarehouses({ ...param, is_site: true })
                }
                onSelect={(val) => setWarehouseId(val.id)}
                labelKey="name"
                placeholder={warehouse ? warehouse : "Select Warehouse"}
                disabled={isDisabled}
              />
              </div>)}
            {mode === STOCK_MODAL_MODES.EDIT && (
            <div className="w-[50%] mb-3">
              <label className="block text-gray-700 font-medium md:text-sm text-xs mb-1">
                MRR Code <sub className="text-red-500">*</sub>
              </label>
              <input
                type="text"
                value={formData.mrr_code}
                className={`w-full border border-gray-300 ${isDisabled ? "bg-gray-100" : ""
                  } text-gray-700 rounded-lg px-3 py-2 focus:ring-1 focus:ring-blue-500 focus:outline-none resize-none`}
                disabled={isDisabled}
              />
            </div>)}
            <div className="w-[50%]">
              <label className="block text-gray-700 font-medium md:text-sm text-xs mb-1">
                Expected Delivery Date <sub className="text-red-500">*</sub>
              </label>
              <input
                type="date"
                value={formData.estimated_delivery_date}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    estimated_delivery_date: e.target.value,
                  }))
                }
                className={`w-full border border-gray-300 ${
                  isDisabled ? "bg-gray-100" : ""
                } text-gray-700 rounded-lg px-3 py-2 focus:ring-1 focus:ring-blue-500 focus:outline-none resize-none`}
                disabled={isDisabled}
              />
            </div>
          </div>
          {/* Notes Field */}
          <div className="mb-4">
            <label className="block text-gray-700 font-medium md:text-sm text-xs my-2">
              Notes <sub className="text-red-500">*</sub>
            </label>
            <textarea
              rows="2"
              value={formData.notes}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  notes: e.target.value,
                }))
              }
              placeholder="Write any notes about this return..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-1 focus:ring-blue-500 focus:outline-none resize-none"
              disabled={mode === STOCK_MODAL_MODES.VIEW}
            ></textarea>
          </div>
          <div className="flex items-center justify-between gap-4">
            <div className="mb-4">
              <label className="block text-gray-700 font-medium md:text-sm text-xs py-1.5">
                Priority <sub className="text-red-500">*</sub>
              </label>
              {mode === STOCK_MODAL_MODES.ADD ? (
                <select
                  value={formData.priority}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      priority: e.target.value,
                    }))
                  }
                  className="w-full border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 focus:outline-none resize-none"
                  disabled={mode === STOCK_MODAL_MODES.VIEW}
                >
                  {" "}
                  <option>Select Priority</option>
                  {PRIORITY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}{" "}
                </select>
              ) : (
                <div>
                  <span
                    className={`py-1.5 px-3 text-sm md:font-medium rounded-lg ${getPriorityStatusColor(
                      formData.priority
                    )}`}
                  >
                    {toProperCase(formData.priority || "")}
                  </span>
                </div>
              )}
            </div>
            {mode !== STOCK_MODAL_MODES.ADD && status && (
              <div className="mb-4 flex flex-col">
                <label className="block text-gray-700  font-medium md:text-sm text-xs mb-1">
                  Status
                </label>
                <span
                  className={`py-1 px-3 font-semibold rounded-lg ${getRNStatusColor(
                    status
                  )}`}
                >
                  {RETURN_NOTE_STATUS_LABELS[status]}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Right Side — Selected Materials */}
        <div>
          <h3 className="text-lg font-semibold text-primary mb-4 flex items-center text-center gap-2">
            <span className="inline-block w-1.5 h-6 bg-primary rounded-full"></span>
            Selected Materials
          </h3>
          {!(mode === STOCK_MODAL_MODES.VIEW) && (
            <>
              <label className="block text-gray-700 font-medium text-xs md:text-sm mb-1">
                Add Product <sub className="text-red-500">*</sub>
              </label>
              <ProductCategoryFilter
                  onProductSelect={handleAddMaterial}
                />
              <label className="block text-gray-700 font-medium text-xs md:text-sm my-2">
                Selected Product List
              </label>
            </>
          )}

          {materials.length === 0 ? (
            <div className="text-gray-600 text-center py-11 border rounded-lg">
              No materials selected yet.
            </div>
          ) : (
            <div
              className={`overflow-x-auto ${
                mode === STOCK_MODAL_MODES.ADD ? "h-[26vh]" : "h-[23vh]"
              } md:h-[40vh] overflow-y-auto`}
            >
              {loading ? (
                <div className="h-full w-full flex justify-center items-center">
                  <div className=" animate-spin border-t border-blue-600"></div>
                </div>
              ) : (
                <GenericTable
                  tableColumns={
                    mode === STOCK_MODAL_MODES.VIEW
                      ? MATERIAL_COLUMNS_MRR.filter((col) => col !== "actions")
                      : MATERIAL_COLUMNS_MRR
                  }
                  tableRows={materials}
                  columnLabels={MATERIAL_LABELS_MRR}
                  renderField={renderField}
                  enableMobileCardView={false}
                />
              )}
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 mt-6">
            {/* Project Engineer / Site Engineer — add or submit */}
            <RoleValidator
              allowedRoles={[ROLES.PROJECT_ENGINEER, ROLES.SITE_ENGINEER]}
              mode="hide"
            >
              {(mode === STOCK_MODAL_MODES.ADD ||
                mode === STOCK_MODAL_MODES.EDIT) && (
                <>
                  <button
                    onClick={() => handleSubmit(MRR_STATUS.DRAFT)}
                    className="px-4 py-2 bg-gray-200 rounded-md text-gray-800 hover:bg-gray-300"
                  >
                    Save as Draft
                  </button>
                  <button
                    onClick={() =>
                      handleSubmit(MRR_STATUS.PENDING_FOR_APPROVAL)
                    }
                    className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark"
                  >
                    Submit for Approval
                  </button>
                </>
              )}
            </RoleValidator>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateMRRs;
